import type * as GeoJSON from 'geojson';
import { defineStore } from 'pinia';

import type {
  FileResultDTO,
  TypesenseSearchResponseDTO,
} from '@nora-monorepo/nora-api-client';
import {
  appLogger,
  useApiStore,
  useGenericUserStore,
  UserLoggingContext,
} from '@nora-monorepo/ui-shared';

type FileSearchPolygon = GeoJSON.Feature<
  GeoJSON.Polygon,
  GeoJSON.GeoJsonProperties
>;

export const useFilesApiStore = defineStore('filesApiStore', () => {
  const api = useApiStore();
  const userStore = useGenericUserStore();

  async function fetchFilesGeoJson(
    polygon: FileSearchPolygon,
    operationId?: string,
  ): Promise<GeoJSON.FeatureCollection> {
    const outerRing = polygon.geometry.coordinates[0];

    const polygonString = outerRing
      .map((coordinates) => {
        const [longitude, latitude] = coordinates;
        if (latitude === undefined || longitude === undefined) {
          return null;
        }

        return `${latitude.toFixed(5)},${longitude.toFixed(5)}`;
      })
      .filter((polygonCoordinate): polygonCoordinate is string => polygonCoordinate !== null)
      .join(',');

    if (polygonString === '') {
      return { type: 'FeatureCollection', features: [] };
    }

    try {
      const result = await api.unwrap(
        api.file.searchFilesInPolygon({
          polygon: polygonString,
          operationId,
        }),
      );

      const data = result.data as TypesenseSearchResponseDTO;

      return convertToGeoJson(data);
    }
    catch (error) {
      appLogger.error({
        message: 'Fetching files failed',
        context: {
          user: new UserLoggingContext({ userId: userStore.user?.id ?? 'unknown', username: userStore.user?.username ?? 'unknown' }),
        },
        sensitiveContext: {
          error: {
            message: error instanceof Error ? error.message : 'Unknown error',
            name: error instanceof Error ? error.name : 'Unknown error',
            stack: error instanceof Error ? error.stack : 'No stack trace',
          },
        },
      });

      return { type: 'FeatureCollection', features: [] };
    }
  }

  function convertToGeoJson(
    data?: TypesenseSearchResponseDTO,
  ): GeoJSON.FeatureCollection {
    if (data === undefined || data.hits === undefined) {
      return { type: 'FeatureCollection', features: [] };
    }

    const features: GeoJSON.Feature[] = [];

    data.hits.forEach((hit) => {
      const file = hit.document as FileResultDTO;

      if (!file.sensorLocation) {
        return;
      }

      features.push({
        type: 'Feature',
        properties: {
          id: file.id,
          domain: file.domain,
          domainId: file.domainId,
          path: file.path,
        },
        geometry: {
          type: 'Point',
          coordinates: [
            file.sensorLocation.longitude,
            file.sensorLocation.latitude,
          ],
        },
      });
    });

    return {
      type: 'FeatureCollection',
      features,
    };
  }

  return { fetchFilesGeoJson };
});
